import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Send, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const SurveyBuilder = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    questions: [],
    target_audience: {
      type: 'whole_company',
      departments: [],
      employee_ids: []
    },
    due_date: '',
    is_anonymous: false
  });

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchSurveys();
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/surveys/manager/all`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      setSurveys(data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const uniqueDepts = [...new Set(data.map(emp => emp.department).filter(Boolean))];
      setDepartments(uniqueDepts);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q-${Date.now()}`,
      question_text: '',
      question_type: 'multiple_choice',
      options: [],
      required: true
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
  };

  const updateQuestion = (index, field, value) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index][field] = value;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const deleteQuestion = (index) => {
    const updatedQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const addOption = (questionIndex) => {
    const updatedQuestions = [...formData.questions];
    if (!updatedQuestions[questionIndex].options) {
      updatedQuestions[questionIndex].options = [];
    }
    updatedQuestions[questionIndex].options.push({
      id: `opt-${Date.now()}`,
      text: ''
    });
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options[optionIndex].text = value;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const deleteOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const saveDraft = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = currentSurvey
        ? `${process.env.REACT_APP_BACKEND_URL}/api/surveys/${currentSurvey.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/surveys/create`;
      
      const method = currentSurvey ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Survey draft saved successfully!');
        fetchSurveys();
        resetForm();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error saving survey:', error);
      alert('Failed to save survey');
    }
    setLoading(false);
  };

  const publishSurvey = async () => {
    // First save as draft
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create or update survey
      let surveyId = currentSurvey?.id;
      
      if (!surveyId) {
        const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/surveys/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!createResponse.ok) {
          const error = await createResponse.json();
          alert(`Error: ${error.detail}`);
          setLoading(false);
          return;
        }
        
        const createData = await createResponse.json();
        surveyId = createData.survey_id;
      }

      // Publish survey
      const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/surveys/${surveyId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (publishResponse.ok) {
        alert('Survey published successfully!');
        fetchSurveys();
        resetForm();
      } else {
        const error = await publishResponse.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error publishing survey:', error);
      alert('Failed to publish survey');
    }
    setLoading(false);
  };

  const deleteSurvey = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/surveys/${surveyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Survey deleted successfully');
        fetchSurveys();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error deleting survey:', error);
    }
  };

  const editSurvey = (survey) => {
    setCurrentSurvey(survey);
    setFormData({
      title: survey.title,
      description: survey.description,
      questions: survey.questions,
      target_audience: survey.target_audience,
      due_date: survey.due_date,
      is_anonymous: survey.is_anonymous
    });
    setShowBuilder(true);
  };

  const resetForm = () => {
    setCurrentSurvey(null);
    setFormData({
      title: '',
      description: '',
      questions: [],
      target_audience: {
        type: 'whole_company',
        departments: [],
        employee_ids: []
      },
      due_date: '',
      is_anonymous: false
    });
    setShowBuilder(false);
  };

  const viewResults = (surveyId) => {
    window.location.href = `/admin/survey-results/${surveyId}`;
  };

  if (showBuilder) {
    return (
      <div className="space-y-6 overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <button
              onClick={resetForm}
              className="flex items-center text-gray-600 hover:text-gray-900 text-sm sm:text-base"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Surveys
            </button>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {currentSurvey ? 'Edit Survey' : 'Create New Survey'}
            </h2>
          </div>

            {/* Survey Details */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Survey Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter survey title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Describe the purpose of this survey"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={formData.target_audience.type}
                  onChange={(e) => setFormData({
                    ...formData,
                    target_audience: {
                      ...formData.target_audience,
                      type: e.target.value,
                      departments: [],
                      employee_ids: []
                    }
                  })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="whole_company">Whole Company</option>
                  <option value="by_department">By Department</option>
                  <option value="individual">Individual Employees</option>
                </select>
              </div>

              {formData.target_audience.type === 'by_department' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Departments</label>
                  <div className="space-y-2">
                    {departments.map(dept => (
                      <label key={dept} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.departments.includes(dept)}
                          onChange={(e) => {
                            const depts = e.target.checked
                              ? [...formData.target_audience.departments, dept]
                              : formData.target_audience.departments.filter(d => d !== dept);
                            setFormData({
                              ...formData,
                              target_audience: { ...formData.target_audience, departments: depts }
                            });
                          }}
                          className="mr-2"
                        />
                        {dept}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.target_audience.type === 'individual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Employees</label>
                  <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-2">
                    {employees.map(emp => (
                      <label key={emp.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.employee_ids.includes(emp.id)}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...formData.target_audience.employee_ids, emp.id]
                              : formData.target_audience.employee_ids.filter(id => id !== emp.id);
                            setFormData({
                              ...formData,
                              target_audience: { ...formData.target_audience, employee_ids: ids }
                            });
                          }}
                          className="mr-2"
                        />
                        {emp.first_name} {emp.last_name} - {emp.department}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_anonymous}
                    onChange={(e) => setFormData({ ...formData, is_anonymous: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Anonymous Survey</span>
                </label>
              </div>
            </div>

            {/* Questions */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Questions</h3>
                <button
                  onClick={addQuestion}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </button>
              </div>

              <div className="space-y-6">
                {formData.questions.map((question, qIndex) => (
                  <div key={question.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium text-gray-500">Question {qIndex + 1}</span>
                      <button
                        onClick={() => deleteQuestion(qIndex)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <input
                        type="text"
                        value={question.question_text}
                        onChange={(e) => updateQuestion(qIndex, 'question_text', e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                        placeholder="Enter your question"
                      />

                      <div className="flex items-center space-x-4">
                        <select
                          value={question.question_type}
                          onChange={(e) => updateQuestion(qIndex, 'question_type', e.target.value)}
                          className="px-4 py-2 border rounded-lg"
                        >
                          <option value="multiple_choice">Multiple Choice</option>
                          <option value="checkbox">Checkbox</option>
                          <option value="free_text">Free Text</option>
                        </select>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => updateQuestion(qIndex, 'required', e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Required</span>
                        </label>
                      </div>

                      {/* Options for multiple choice and checkbox */}
                      {(question.question_type === 'multiple_choice' || question.question_type === 'checkbox') && (
                        <div className="ml-4 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Options:</span>
                            <button
                              onClick={() => addOption(qIndex)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              + Add Option
                            </button>
                          </div>
                          {question.options?.map((option, oIndex) => (
                            <div key={option.id} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border rounded"
                                placeholder={`Option ${oIndex + 1}`}
                              />
                              <button
                                onClick={() => deleteOption(qIndex, oIndex)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:space-x-4 mt-6 pt-6 border-t">
              <button
                onClick={saveDraft}
                disabled={loading}
                className="flex items-center justify-center px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm sm:text-base"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </button>
              <button
                onClick={publishSurvey}
                disabled={loading}
                className="flex items-center justify-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm sm:text-base"
              >
                <Send className="w-4 h-4 mr-2" />
                Publish Survey
              </button>
            </div>
          </div>
        </div>
    );
  }

  // Survey List View
  return (
    <div className="space-y-6 overflow-x-hidden">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Surveys</h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage employee surveys</p>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="flex items-center justify-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Survey
          </button>
        </div>
      </div>

      <div className="grid gap-6">
        {surveys.map(survey => (
            <div key={survey.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">{survey.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      survey.status === 'draft' ? 'bg-gray-200 text-gray-700' :
                      survey.status === 'live' ? 'bg-green-200 text-green-700' :
                      'bg-blue-200 text-blue-700'
                    }`}>
                      {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">{survey.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Questions:</span>{' '}
                      <span className="font-medium">{survey.questions?.length || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Responses:</span>{' '}
                      <span className="font-medium">{survey.completed_responses || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Due Date:</span>{' '}
                      <span className="font-medium">{new Date(survey.due_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 lg:flex-col">
                  {survey.status === 'draft' && (
                    <>
                      <button
                        onClick={() => editSurvey(survey)}
                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm sm:text-base"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteSurvey(survey.id)}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm sm:text-base"
                      >
                        Delete
                      </button>
                    </>
                  )}
                  {survey.status === 'live' && (
                    <button
                      onClick={() => viewResults(survey.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                    >
                      View Results
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {surveys.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No surveys yet. Create your first survey!</p>
            </div>
          )}
      </div>
    </div>
  );
};

export default SurveyBuilder;
